import {
  ConfirmPassword,
  Form,
  OperationButton,
  Password,
  SomethingWentWrongFormError,
  Username,
  UsernameField
} from 'components/core-v2'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { usePostUsermanagerConfirmEmailMutation, usePostUsermanagerResetMutation } from '@/api'
import { FIELDS } from '@/components/core-v2/molecules/fields/constants'
import { ConfirmPasswordField, PasswordField } from '@/components/core-v2/molecules/fields/password'
import {
  alphanumeric,
  containsUpperCaseLetter,
  minimumAmountOfNumbers,
  minimumCharacterLength,
  validatePassword
} from '@/components/core-v2/molecules/fields/validators'
import { useLogin } from '@/utilities/hooks/useLogin'

import { ValidationRule } from '../../validation-rule-block'
import { COPY } from './constants'
import classes from './index.module.scss'

const validators = {
  validate: {
    ...minimumCharacterLength(8).validate,
    ...containsUpperCaseLetter.validate,
    ...minimumAmountOfNumbers(2).validate,
    ...alphanumeric.validate
  }
}

export type SetUpPasswordFields = UsernameField & PasswordField & ConfirmPasswordField

const { button, validation } = COPY

interface Props {
  on2FANotSetup?: () => void
  usernameDefaultValue: string
}
export const SetUpPasswordForm = ({ on2FANotSetup, usernameDefaultValue }: Props) => {
  const params = useParams()
  const form = useForm<SetUpPasswordFields>({
    defaultValues: {
      [FIELDS.username.id]: usernameDefaultValue
    }
  })

  const [postConfirmEmail, confirmEmailStatus] = usePostUsermanagerConfirmEmailMutation()
  const [postResetPassword, resetPasswordStatus] = usePostUsermanagerResetMutation()
  const [postLogin, loginStatus] = useLogin({
    redirectOnSuccess: false,
    redirectOnLockedOut: true,
    on2FANotSetup
  })
  const isLoading =
    confirmEmailStatus.isLoading || resetPasswordStatus.isLoading || loginStatus.isLoading
  const isError = confirmEmailStatus.isError || resetPasswordStatus.isError || loginStatus.isError

  const onSubmit = async (values: SetUpPasswordFields) => {
    try {
      const confirmEmailResponse = await postConfirmEmail({
        apiConfirmEmail: {
          confirmEmailToken: params.token,
          userId: params.userId
        }
      })
      if ('error' in confirmEmailResponse) throw new Error()
      await postResetPassword({
        resetPasswordCommand: {
          password: values.password,
          token: confirmEmailResponse.data.passwordResetToken,
          userId: params.userId
        }
      })
      await postLogin({
        password: values.password,
        username: params.username
      })
    } catch (e) {
      /* empty */
    }
  }

  const [passwordValue, confirmPasswordValue] = form.watch(['password', 'confirmPassword'])
  const arePasswordsMatching = passwordValue === confirmPasswordValue
  const { hasMinimumCharacters, hasOneUppercaseLetter, hasTwoNumbers, isAlphanumeric } =
    validatePassword(passwordValue)

  return (
    <div>
      <FormProvider {...form}>
        <Form
          noValidate
          aria-busy={isLoading}
          data-testid="set-up-password-form"
          name="Set up password form"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <Username isDisabled />
          <Password
            aria-describedby="password-validations"
            data-testid="password-field"
            isDisabled={isLoading}
            validators={validators}
          />
          <ConfirmPassword
            aria-describedby="password-validations"
            data-testid="confirm-password-field"
            isDisabled={isLoading}
            validators={{ validate: () => arePasswordsMatching || 'Passwords do not match' }}
          />
          <span className={classes.validationHeading}>{validation.heading}</span>
          <ValidationRule.Block id="password-validations">
            <ValidationRule.Rule isCorrect={hasMinimumCharacters} label={validation.characters} />
            <ValidationRule.Rule isCorrect={hasOneUppercaseLetter} label={validation.upperCase} />
            <ValidationRule.Rule isCorrect={hasTwoNumbers} label={validation.numbers} />
            <ValidationRule.Rule isCorrect={isAlphanumeric} label={validation.specialSymbols} />
          </ValidationRule.Block>
          <SomethingWentWrongFormError className="mt-6 mb-5" isShowing={isError} />
          <OperationButton
            className="mt-6"
            isDisabled={isLoading}
            isLoading={isLoading}
            operationType="progress"
            type="submit"
            width="100%"
          >
            {button}
          </OperationButton>
        </Form>
      </FormProvider>
    </div>
  )
}
